import { Switch, TAB_TYPE } from '@repo/design-system/index'
import { useEffect, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import apis from '../../../modules/apis'
import { axiosGet, axiosPost } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { LOCALE_KO } from '../../../modules/i18n/config'
import { n_lesson_prep } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { DATA_REF_KEY } from '../../../modules/ids/e2e/student'
import ResizableImage from '../../common/ResizableImage'
import AnswerItem from './QuestionSelect/AnswerItem'
import MyAnswerHistory from './QuestionSelect/MyAnswerHistory'
import PeopleOpinions from './QuestionSelect/PeopleOpinions'
import QButton from './QuestionSelect/QButton'
import { AnswerSubTab, getShareAnswer } from './QuestionSelect/QuestionSelectPanel'

// 수업 준비 아닌 경우 - 답변 저장하기 버튼 클릭 시 - API 불러서 작성하고 있는 내용을 저장하고
//isEditable 을 false 로 설정해서 수정하기 상태로 갈 수 있도록
const handleSubmitAnswer = async ({ course_id, question_number, content, shareStatus, isEditable, setIsEditable }) => {
  // content 아무것도 없을 수 없음 => 저장하기 버튼 비활성화임
  const payload = {
    course_id: Number(course_id),
    question_number,
    content,
    share_status: shareStatus, // 1: me, 0:public, 2: tutor and me
  }

  const response = await axiosPost(apis.lessons.preparation.answerV5(), payload)
  console.log(response)

  if (response) {
    const responseQuestion = await axiosGet(apis.course.coursekeyquestions(course_id))
    if (responseQuestion && responseQuestion.coursekeyquestions) {
      console.log(responseQuestion)
      setIsEditable(
        responseQuestion.coursekeyquestions.map((question) => ({
          ...question,
          currentAnswer: '', // 현재 보관 중인 answer
          currentAnswerStatus: 0, // 현재 보관 중인 share_status
          editable: true,
        }))
      )
    }
  }
}

export const fetchCourseData = async (courseId) => {
  const response = await axiosGet(apis.course.coursekeyquestions(courseId))

  if (response) {
    return response.coursekeyquestions
  }
}

interface IQuestionPanel {
  id: number
  materialData: any
}
const QuestionPanel = ({ id, materialData }: IQuestionPanel) => {
  const { currentUser } = useCurrentUser()
  const { t, locale } = useTranslation()
  const { sendClickEvent } = useEventTracking()
  const isWebview = useIsWebview()
  const [questionTranslate, setQuestionTranslate] = useState<boolean>(false)
  const [myAnswers, setMyAnswers] = useState([])
  const [shareAnswers, setShareAnswers] = useState([])
  const [shareParticipant, setParticipantList] = useState([])
  const [answerSubTabId, setAnswerSubTab] = useState<string>('my_answer')
  const [isEditable, setIsEditable] = useState([])
  const [answer, setAnswer] = useState('')
  const [selectedShareState, setSelectedShareState] = useState<number>(0)
  const [activeQuestionId, setActiveQuestionId] = useState<number | string>('1')
  const [questions, setQuestions] = useState([])

  // data set - questions
  useEffect(() => {
    const fetchAnswerData = async (courseId) => {
      try {
        const response = await axiosGet(apis.course.coursekeyquestions(courseId))
        if (response && response.coursekeyquestions) {
          console.log(response)

          setIsEditable(
            response.coursekeyquestions.map((question) => ({
              ...question,
              currentAnswer: '', // 현재 보관 중인 answer
              currentAnswerStatus: 0, // 현재 보관 중인 share_status
              editable: true,
            }))
          )
          setAnswer('')
          setSelectedShareState(0)
          setQuestions(response.coursekeyquestions)
        }
      } catch {
        console.log('fetch error')
      }
    }

    fetchAnswerData(id)
  }, [])

  useEffect(() => {
    setAnswer(isEditable?.find((item, idx) => Number(item.question_number) == activeQuestionId)?.currentAnswer)
    setSelectedShareState(
      isEditable?.find((item, idx) => Number(item.question_number) == activeQuestionId)?.currentAnswerStatus
    )
    setMyAnswers(isEditable?.filter((item) => item.question_number == activeQuestionId)[0]?.answers?.my_answers)
    getShareAnswer(
      isEditable?.filter((item) => item.question_number == activeQuestionId)[0]?.id,
      setShareAnswers,
      setParticipantList
    )
  }, [activeQuestionId, isEditable])

  const optionList = [
    {
      key: 'my_answer',
      label: t(n_lesson_prep.answer_my),
      number: myAnswers?.length,
    },
    {
      key: 'other_answer',
      label: t(n_lesson_prep.answer_other),
      number: shareAnswers?.length,
    },
  ]

  const onChangeTab = () => {
    console.log('change tab')

    const updatedIsEditable = isEditable.map((item) => {
      if (Number(item.question_number) == activeQuestionId) {
        return {
          ...item,
          currentAnswer: answer,
          currentAnswerStuatus: selectedShareState,
        }
      }
      return item
    })

    // 새 배열을 사용하여 상태를 업데이트합니다.
    setIsEditable(updatedIsEditable)
    setAnswer('')
    setSelectedShareState(0)
  }

  const handleOpenOpinion = (answers) => {
    console.log('handling open opinion')
  }

  const sortByQuestionNumber = (arr) => {
    return arr.sort((a, b) => parseInt(a.question_number) - parseInt(b.question_number))
  }

  const sortedQuestionList = sortByQuestionNumber(questions)

  return (
    <div className="pb-[100px] sm:pb-[0px]">
      <div className="flex flex-col">
        <div className="mb-[4px]">
          <span className="text-[20px] font-bold text-black">
            {locale == 'ko' ? materialData.course?.title_kr : materialData.course?.title_en}
          </span>
        </div>
        <div className="mb-[12px]">
          <span className="text-[16px] font-medium text-gray-700">
            {locale == 'ko' ? materialData.course?.subtitle_kr : materialData.course?.subtitle_en}
          </span>
        </div>
        <div className="mb-[24px] h-[1px] w-full bg-gray-200"></div>
      </div>
      <div className="mb-[24px] flex gap-x-8 overflow-x-auto sm:flex-wrap sm:gap-y-8">
        {sortedQuestionList.map((item, index) => (
          <div
            data-ref={DATA_REF_KEY.btn_question}
            key={`${index}_${item.id}`}
            onClick={(e) => {
              console.log(item.id)

              getShareAnswer(item.id, setShareAnswers, setParticipantList)
            }}
          >
            <QButton
              key={`${index}_${item.id}`}
              number={item?.question_number}
              checked={isEditable}
              activeQuestionId={activeQuestionId}
              setActiveQuestionId={setActiveQuestionId}
              onChangeTab={onChangeTab}
            />
          </div>
        ))}
      </div>
      {/* 질문카드 */}
      <div className="mt-[4px]">
        <div key={`question-${activeQuestionId}`}>
          <div>
            <div>
              {locale == LOCALE_KO && (
                <div className="mb-[8px] flex">
                  {isEditable?.find((item) => item.question_number == activeQuestionId)?.content_main_ko && (
                    <div className="ml-auto flex items-center space-x-4">
                      <span className="text-14 font-medium text-gray-700">{t(n_lesson_prep.view_kr)}</span>
                      <div
                        onClick={() => {
                          // 질문 한글로 보기 toggle event tracking
                          sendClickEvent({
                            button_name: 'lesson_question_lang_toggle',
                            object_title: !questionTranslate ? 'on' : 'off',
                          })
                        }}
                      >
                        <Switch checked={questionTranslate} setChecked={setQuestionTranslate} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="text-h2 mb-[8px] font-bold">
                {isEditable.find((item) => item.question_number === activeQuestionId)?.content_main}
              </div>
              {questionTranslate && (
                <div className="mb-[16px] text-[16px] font-medium text-gray-500">
                  {isEditable?.find((item) => item.question_number == activeQuestionId)?.content_main_ko}
                </div>
              )}
              {isEditable.find((item) => item.question_number === activeQuestionId)?.img_url_1 &&
                isEditable.find((item) => item.question_number === activeQuestionId)?.img_url_1 !== '' && (
                  <ResizableImage
                    url={isEditable.find((item) => item.question_number === activeQuestionId)?.img_url_1}
                  />
                )}
            </div>
            <div style={{ height: 1 }} className="my-[20px] bg-gray-200"></div>
            <div>
              {/* 내 답변 */}
              <AnswerItem
                answer={answer}
                setAnswer={setAnswer}
                isEditable={isEditable}
                setIsEditable={setIsEditable}
                activeQuestionId={Number(activeQuestionId)}
                lessonId={id}
                handleSubmitAnswer={handleSubmitAnswer}
                onBlur={null}
                currentUser={currentUser}
                isReview={false}
                isLesson={false}
                isPast={() => {
                  return false
                }}
                selectedShareState={selectedShareState}
                setSelectedShareState={setSelectedShareState}
              />
            </div>
            {/** webview 일때는 회색 선이 없어야 함 */}
            {!isWebview && <div style={{ height: 8 }} className="mt-[40px] bg-gray-100"></div>}
            {isWebview && <div className="h-[36px]"></div>}
          </div>
        </div>
      </div>
      {/** 답변 확인 */}
      <div>
        <AnswerSubTab
          optionList={optionList}
          activeKey={answerSubTabId}
          setActiveKey={setAnswerSubTab}
          type={TAB_TYPE.SCROLL}
        />
        {answerSubTabId === 'my_answer' ? (
          <MyAnswerHistory
            isEditable={isEditable}
            myAnswers={myAnswers}
            setMyAnswers={setMyAnswers}
            id={id}
            activeQuestionId={activeQuestionId}
          />
        ) : (
          <PeopleOpinions
            answers={shareAnswers}
            participantList={shareParticipant}
            handleOpenOpinion={handleOpenOpinion}
            tutorAnswerComponent={null}
          />
        )}
      </div>
    </div>
  )
}

export default QuestionPanel
